/**
 * Modo de cor (claro / escuro / igual ao sistema) — eixo SEPARADO do
 * tema de paleta (`reputation_theme` / `data-theme`).
 *
 * O tema pinta a casca (menu, topbar) e o destaque; o modo pinta o
 * painel de conteúdo (superfícies, texto, bordas). Por isso são dois
 * atributos no `<html>`: `data-theme="<paleta>"` e `data-mode="dark"`.
 * Ver `tokens.css` (bloco `:root[data-mode="dark"]`).
 *
 * Como o tema, mora no `localStorage` do navegador e não passa pela API.
 * O `index.html` repete a leitura num script inline, antes do React,
 * para a primeira pintura já sair no modo certo — sem isso a tela
 * piscava branca ao recarregar no escuro. Mexeu na chave ou nos valores
 * aqui, mexe lá também.
 */

export type ColorMode = "light" | "dark" | "system";

export const COLOR_MODE_STORAGE_KEY = "reputation_color_mode";

const MODES: readonly ColorMode[] = ["light", "dark", "system"];
const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Sem escolha salva, fica no claro — o visual de antes do modo existir. */
export function readColorMode(): ColorMode {
  try {
    const saved = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    return MODES.includes(saved as ColorMode) ? (saved as ColorMode) : "light";
  } catch {
    return "light";
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches;
}

export function resolveColorMode(mode: ColorMode): "light" | "dark" {
  if (mode === "system") return systemPrefersDark() ? "dark" : "light";
  return mode;
}

export function applyColorMode(mode: ColorMode): void {
  const root = document.documentElement;
  if (resolveColorMode(mode) === "dark") {
    root.setAttribute("data-mode", "dark");
  } else {
    root.removeAttribute("data-mode");
  }
}

export function saveColorMode(mode: ColorMode): void {
  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  } catch {
    // Navegador sem storage (aba privada restrita): o modo vale só
    // até recarregar, que é o melhor possível.
  }
  applyColorMode(mode);
}

/** No modo "sistema", acompanha a troca do sistema operacional com a
 *  tela aberta. Devolve a função que desliga o ouvinte. */
export function followSystemColorMode(): () => void {
  if (typeof window.matchMedia !== "function") return () => undefined;
  const query = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (readColorMode() === "system") applyColorMode("system");
  };
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
